import React, { useState, useEffect, useRef } from 'react';
import AccountLayout from './AccountLayout';
import authService from '../../utils/authService';
import { strapiService } from '../../utils/api';
import AlertService from '../../utils/alertService';
import { Badge, Button, Note, SkeletonRows, TabsUnderline } from '../../components/app2';
import UserProfileForm from './components/UserProfileForm';
import PasswordChangeSection from './components/PasswordChangeSection';
import AccountSettingsSection from './components/AccountSettingsSection';
import SecuritySection from './components/SecuritySection';
import PreferencesSection from './components/PreferencesSection';

/**
 * Mi perfil (rediseño jul-2026).
 *
 * Consolida las DOS pantallas de perfil que existían: esta y
 * /app/user-profile-management, que montaba exactamente los mismos componentes.
 * El inventario del rediseño ya señalaba la duplicidad; ahora hay una sola,
 * organizada en pestañas como propone el diseño:
 *
 *   Datos        → información personal + configuración de la cuenta
 *   Seguridad    → cambio de contraseña + estado de seguridad
 *   Preferencias → tema de la interfaz (claro · automático · oscuro)
 *
 * Las preferencias de notificaciones que dibuja el diseño NO entran: no existen
 * ni el campo donde guardarlas ni el envío que las respetaría.
 *
 * La ruta antigua redirige aquí, así que ningún enlace guardado se rompe.
 */

const TABS = [
  { id: 'datos', label: 'Datos' },
  { id: 'seguridad', label: 'Seguridad' },
  { id: 'preferencias', label: 'Preferencias' },
];

const ROLE_TITLE = {
  super_admin: 'Super Administrador',
  company_admin: 'Administrador de Empresa',
  employee: 'Empleado',
};

const AccountProfile = () => {
  const [tab, setTab] = useState('datos');
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const profileChangesRef = useRef({});

  useEffect(() => {
    loadUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setUserData(authService?.getCurrentUser());
      const profileResponse = await authService?.getUserProfile();
      if (profileResponse?.user) setProfileData(profileResponse);
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!hasChanges) return;
    try {
      setLoading(true);
      AlertService?.loading('Guardando cambios...', 'Por favor espere.');
      if (Object.keys(profileChangesRef.current).length > 0) {
        await strapiService?.auth?.updateUserProfile(profileChangesRef.current);
      }
      await loadUserProfile();
      profileChangesRef.current = {};
      setHasChanges(false);
      AlertService?.close();
      await AlertService?.success('Perfil actualizado', 'Los cambios han sido guardados', {
        autoClose: true,
      });
    } catch (error) {
      AlertService?.close();
      await AlertService?.error(
        'Error al guardar',
        error?.response?.data?.error?.message || 'Error al guardar los cambios.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProfileDataChange = (field, value) => {
    profileChangesRef.current[field] = value;
    setHasChanges(true);
  };

  return (
    <AccountLayout
      title="Mi perfil"
      subtitle="Administra tu información personal y la seguridad de tu cuenta"
      headerActions={
        <Badge tone="brand" icon="shield_person">
          {ROLE_TITLE[userData?.user_type] || 'Usuario'}
        </Badge>
      }
    >
      {loading && !userData ? (
        <SkeletonRows rows={5} />
      ) : (
        <>
          <TabsUnderline items={TABS} value={tab} onChange={setTab} style={{ marginBottom: 22 }} />

          {tab === 'datos' ? (
            <div className="a2-anim-fadein" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <UserProfileForm
                userData={userData}
                profileData={profileData}
                onDataChange={handleProfileDataChange}
              />
              <AccountSettingsSection userData={userData} profileData={profileData} />

              {/* Barra de guardado: solo actúa cuando hay algo que guardar */}
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  flexWrap: 'wrap', gap: 12, paddingTop: 4,
                }}
              >
                {hasChanges ? (
                  <Note tone="warn" style={{ marginRight: 'auto', padding: '9px 14px' }}>
                    Tienes cambios sin guardar
                  </Note>
                ) : null}
                <Button
                  icon="save"
                  onClick={handleSaveChanges}
                  disabled={loading || !hasChanges}
                  loading={loading && hasChanges}
                >
                  Guardar cambios
                </Button>
              </div>
            </div>
          ) : tab === 'seguridad' ? (
            <div className="a2-anim-fadein" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <PasswordChangeSection />
              <SecuritySection userData={userData} profileData={profileData} />
            </div>
          ) : (
            <div className="a2-anim-fadein" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <PreferencesSection />
            </div>
          )}
        </>
      )}
    </AccountLayout>
  );
};

export default AccountProfile;
